import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { HiOutlineKey, HiOutlineClipboardDocument } from "react-icons/hi2";
import { FormButton, ModalDialog } from "../../ui";
import { handleFailedRequest, notify } from "../../util";
import { IResponseError } from "../../data-access";
import { developerApi } from "../../api/developer-tools";
import { Input } from "antd";

import "./upwex-api.scss";

interface IUpwexApiKeyModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const baseClass = "upwex-api-modal";

export const UpwexApiKeyModal = ({
  onClose,
  onSuccess,
}: IUpwexApiKeyModalProps): JSX.Element => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [openKey, setOpenKey] = useState<string>("");
  const inputEl = useRef(null);

  const handleCopy = (ref: any) => {
    ref.select();
    ref.setSelectionRange(0, 99999);
    navigator.clipboard.writeText(openKey);
    notify("success", "API key has been copied!");
  };

  const handleCreateKey = (): void => {
    setIsProcessing(true);
    developerApi
      .createKey()
      .then((response) => {
        const {
          data: { access_key },
        } = response;
        setOpenKey(access_key);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessing(false));
  };

  const handleClose = (): void => {
    if (!isProcessing) {
      onClose();
    }
  };

  const handleDone = () => {
    onClose();
    onSuccess && onSuccess();
  };

  return (
    <ModalDialog
      className={`${baseClass}-dialog`}
      isOpen={true}
      onCancel={handleClose}
      closable={false}
    >
      <div className={baseClass}>
        <div className={`${baseClass}__icon`}>
          <HiOutlineKey size={26} />
        </div>

        {!openKey ? (
          <>
            <h3 className={`${baseClass}__title`}>Create a New API Key</h3>
            <p className={`${baseClass}__text`}>
              Generate a new API key to access the Upwex API. Make sure to store
              your key securely, as it grants access to important data and
              functionality.
            </p>
            <div className={`${baseClass}__buttons`}>
              <FormButton theme="grey" onClick={handleClose}>
                Cancel
              </FormButton>
              <FormButton
                theme="green-light"
                disabled={isProcessing}
                loading={isProcessing}
                onClick={handleCreateKey}
              >
                Create Key
              </FormButton>
            </div>
          </>
        ) : (
          <>
            <h3 className={`${baseClass}__title`}>Save Your Key</h3>
            <p className={`${baseClass}__text`}>
              Please save this secret key somewhere safe. For security reasons,{" "}
              <b>you won&apos;t be able to view it again</b>. If you lose it,
              you&apos;ll need to generate a new one.
            </p>
            <div className={`${baseClass}__key-field`}>
              <Input
                className={`${baseClass}__key-input`}
                value={openKey}
                readOnly
                ref={inputEl}
              />
              <button
                className={`${baseClass}__copy-btn`}
                onClick={() => handleCopy(inputEl.current)}
              >
                <HiOutlineClipboardDocument size={18} />
                Copy
              </button>
            </div>
            <div className={`${baseClass}__buttons`}>
              <FormButton theme="green-light" onClick={handleDone}>
                Done
              </FormButton>
            </div>
          </>
        )}
      </div>
    </ModalDialog>
  );
};

UpwexApiKeyModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
